import _ from "lodash";
import * as C from "../Common";
import * as S from "../services";
import * as PM from "../PurposeModal";
import * as M from "../Components/Modal";
import { FP, T, TB, TC } from "../Constants";
import * as US from "../services/user.service";
import { store as async_store } from "../Redux";

/** Some special tags to handle for the ULB import */
type ExtraProps = "rm_tag" | "dv_tag" | "dp_tag" | "mail_tag" | "site" | "building" | "zone" | "floor" | "local";
const extra_props: ExtraProps[] = ["rm_tag", "dv_tag", "dp_tag", "mail_tag", "site", "building", "zone", "floor", "local"];
const tags_props: ExtraProps[] = ["dp_tag", "dv_tag", "mail_tag", "rm_tag"];
/** The format of an emplacement after it's been mapped */
type EmplacementRow = T.EmplacementData & Record<ExtraProps, string>;
/** The function to import a file of new equipments. Catch sends back, argument for a Modal ALert */
type ImportEmplacement = (context?: T.ContextParams) => Promise<"cancelled" | "empty" | ReturnType<T.API.Utils.Tables.BulkEmplacements>>;
/** How to format the tags to create */
const TagAddOn: Partial<Record<ExtraProps, string>> = { rm_tag: "rm_type: ", dp_tag: "dp_id: ", dv_tag: "dv_id: ", mail_tag: "Tag Mail: " };

const local_import: ImportEmplacement = context => new Promise(async (resolve, reject) => {
    // Find the site to work on
    const client_root_promise = new Promise<string>(resolve => {
        if (context) PM.renderClientSelect({ isRequired: true, context: context })
            .then(build => resolve(build));
        else M.renderSearchContext({ searchedType: "client" })
            .then(pick => resolve(pick?.id));
    });

    const store = await async_store
    // The user has chosen a site
    const client = await client_root_promise

    //#region Prepare the languages
    const redux_state = store.getState(),
        translations = redux_state.storedLanguages,
        lang = localStorage.getItem("lang") || "fr_fr" as T.LanguageProps,
        equip_form = redux_state.formIds.find(f => f.path === FP.EMPLACEMENT_FORM)?._id,
        equip_translations = translations.object.filter(t => t._id === equip_form);
    // Get the user id
    const auth = await redux_state.auth;
    const user_id = auth.user?._id;

    const col_name = (property: string): string => {
        let prop_tr = equip_translations.find(t => t.prop === property);
        if (prop_tr) return prop_tr[lang] || property;
        return translations.static.find(tr => tr.ref === property)?.[lang] || property;
    }
    //#endregion

    if (!TB.mongoIdValidator(client)) resolve("cancelled");
    else {
        // Create the structure of the fields to import
        const columns_format = {
            name: { label: "", type: "string", required: true },
            site: { label: TC.EMP_IMPORT_COL_SITE, type: "string", required: true },
            building: { label: TC.EMP_IMPORT_COL_BUILDING, type: "string", required: true },
            zone: { label: TC.EMP_IMPORT_COL_ZONE_1, type: "string" },
            floor: { label: TC.EMP_IMPORT_COL_FLOOR, type: "string" },
            local: { label: TC.EMP_IMPORT_COL_LOCAL, type: "string" },
            rm_tag: { label: "Tag RM", type: "string" },
            dv_tag: { label: "Tag DV", type: "string" },
            dp_tag: { label: "Tag DP", type: "string" },
            mail_tag: { label: "Tag Mail", type: "string" },
            actif_detail: { label: "", type: "string" },
            area: { label: "", type: "number" },
            selling: { label: "", type: "boolean" },
            capacity: { label: "", type: "number" },
            surfaceLA: { label: "", type: "number" },
            surfaceTA: { label: "", type: "number" },
            surfaceCA: { label: "", type: "number" },
            surfaceAA: { label: "", type: "number" },
            surfacePA: { label: "", type: "number" },
            criticity: { label: "", type: "string" },
            employees: { label: "", type: "boolean" },
            surfaceNFA: { label: "", type: "number" },
            surfaceIFA: { label: "", type: "number" },
            surfaceNRA: { label: "", type: "number" },
            surfaceUTA: { label: "", type: "number" },
            surfaceUCA: { label: "", type: "number" },
            surfaceUAA: { label: "", type: "number" },
            surfaceUPA: { label: "", type: "number" },
            surfaceBHS: { label: "", type: "number" },
            surfaceCold: { label: "", type: "number" },
            isRentable: { label: "", type: "boolean" },
            surfaceRENT: { label: "", type: "number" },
            floorNumber: { label: "", type: "number" },
            surfaceHEAT: { label: "", type: "number" },
            parking_type: { label: "", type: "string" },
            isOccupation: { label: "", type: "boolean" },
            surfaceRENTED: { label: "", type: "number" },
            requiresPDERBC: { label: "", type: "boolean" },
            asbestosWorksRBC: { label: "", type: "boolean" },
            grandeEntrepriseVLA: { label: "", type: "boolean" },
            grandeEntrepriseRBCWA: { label: "", type: "boolean" },
        } as T.ImportFormat<keyof EmplacementRow>;

        // Set the label of each columns
        for (let [key, value] of Object.entries(columns_format)) columns_format[key].label = col_name(value.label || key);

        // Set the params for the file import and the mapping of the properties
        const mapper_params = {
            required: true,
            save: "local_import",
            columnFormat: columns_format,
            title: TC.GLOBAL_SELECT_FILE_IMPORT,
        } as Parameters<typeof M.renderExcelMapper>[0];

        // File import + user mapping
        const excel_emplacement = await M.renderExcelMapper(mapper_params) as EmplacementRow[];

        if (!excel_emplacement) resolve("cancelled");
        else if (excel_emplacement.length === 0) resolve("empty");
        else {
            // Determine which values are parasites
            const ignored_list = [] as C.Import.ValueIgnorerProps['values'];
            const add_to_list = (prop: string, value: string) => {
                // Pair prop/value not already in the list
                if (!ignored_list.some(i => i.prop === prop && i.value === value)) {
                    let label = columns_format[prop]?.label || prop;
                    ignored_list.push({ prop, value, label, ignored: true });
                }
            }

            // Load the resources that will be useful for the mapping of the data
            const dismount = M.renderLoader();
            const emp_resources_data = await S.getEmplacementImportResources(client);
            dismount();

            /** Keep track of the tags to create */
            const users_array = TB.mongoIdValidator(user_id) ? [user_id] : [];
            /** Keep track of the existing tags that were updated */
            const updated_existing_tags = [] as string[];

            const tags_mapper = [] as (Record<"tag_name" | "tag_id", string> & Record<"tag_type", T.NoteTag["type"]> & Record<"site_names" | "site_ids" | "users", string[]>)[];
            emp_resources_data.data.tags.forEach(t => tags_mapper.push({
                tag_id: t.value,
                users: t.users,
                tag_type: t.type,
                site_ids: t.sites,
                tag_name: t.label,
                site_names: t.sites,
            }));

            // Check for parasite values
            for (const emplacement of excel_emplacement) {
                // If floor label was picked up as a number, set it to a string
                if (typeof emplacement.floor === "number") emplacement.floor = (emplacement.floor as number).toString();
                // If local label was picked up as a number, set it to a string
                if (typeof emplacement.local === "number") emplacement.local = (emplacement.local as number).toString();
                // If zone label was picked up as a number, set it to a string
                if (typeof emplacement.zone === "number") emplacement.zone = (emplacement.zone as number).toString();
                // If building label was picked up as a number, set it to a string
                if (typeof emplacement.building === "number") emplacement.building = (emplacement.building as number).toString();
                // If site label was picked up as a number, set it to a string
                if (typeof emplacement.site === "number") emplacement.site = (emplacement.site as number).toString();

                // Start mapping the tags
                if (emplacement.dp_tag) {
                    const tag_name = TagAddOn.dp_tag + emplacement.dp_tag;
                    const already_mapped = tags_mapper.find(t => t.tag_type === "emplacement" && t.tag_name === tag_name);
                    if (!already_mapped) tags_mapper.push({ users: users_array, site_names: [emplacement.site], site_ids: [], tag_id: "", tag_name: tag_name, tag_type: "emplacement" });
                    else {
                        if (!already_mapped.users.includes(user_id) && user_id) already_mapped.users.push(user_id);
                        if (!already_mapped.site_names.includes(emplacement.site)) already_mapped.site_names.push(emplacement.site);
                        if (!updated_existing_tags.includes(already_mapped.tag_id)) updated_existing_tags.push(already_mapped.tag_id);
                    }
                }
                if (emplacement.dv_tag) {
                    const tag_name = TagAddOn.dv_tag + emplacement.dv_tag;
                    const already_mapped = tags_mapper.find(t => t.tag_type === "emplacement" && t.tag_name === tag_name);
                    if (!already_mapped) tags_mapper.push({ users: users_array, site_names: [emplacement.site], site_ids: [], tag_id: "", tag_name: tag_name, tag_type: "emplacement" });
                    else {
                        if (!already_mapped.users.includes(user_id) && user_id) already_mapped.users.push(user_id);
                        if (!already_mapped.site_names.includes(emplacement.site)) already_mapped.site_names.push(emplacement.site);
                        if (!updated_existing_tags.includes(already_mapped.tag_id)) updated_existing_tags.push(already_mapped.tag_id);
                    }
                }
                if (emplacement.rm_tag) {
                    const tag_name = TagAddOn.rm_tag + emplacement.rm_tag;
                    const already_mapped = tags_mapper.find(t => t.tag_type === "emplacement" && t.tag_name === tag_name);
                    if (!already_mapped) tags_mapper.push({ users: users_array, site_names: [emplacement.site], site_ids: [], tag_id: "", tag_name: tag_name, tag_type: "emplacement" });
                    else {
                        if (!already_mapped.users.includes(user_id) && user_id) already_mapped.users.push(user_id);
                        if (!already_mapped.site_names.includes(emplacement.site)) already_mapped.site_names.push(emplacement.site);
                        if (!updated_existing_tags.includes(already_mapped.tag_id)) updated_existing_tags.push(already_mapped.tag_id);
                    }
                }
                if (emplacement.mail_tag) {
                    const tag_name = TagAddOn.mail_tag + emplacement.mail_tag;
                    const already_mapped = tags_mapper.find(t => t.tag_type === "emplacement" && t.tag_name === tag_name);
                    if (!already_mapped) tags_mapper.push({ users: users_array, site_names: [emplacement.site], site_ids: [], tag_id: "", tag_name: tag_name, tag_type: "emplacement" });
                    else {
                        if (!already_mapped.users.includes(user_id) && user_id) already_mapped.users.push(user_id);
                        if (!already_mapped.site_names.includes(emplacement.site)) already_mapped.site_names.push(emplacement.site);
                        if (!updated_existing_tags.includes(already_mapped.tag_id)) updated_existing_tags.push(already_mapped.tag_id);
                    }
                }
                if (emplacement.actif_detail) {
                    const tag_name = emplacement.actif_detail;
                    const already_mapped = tags_mapper.find(t => t.tag_type === "building" && t.tag_name === tag_name);
                    if (!already_mapped) tags_mapper.push({ users: users_array, site_names: [emplacement.site], site_ids: [], tag_id: "", tag_name: tag_name, tag_type: "building" });
                    else {
                        if (!already_mapped.users.includes(user_id) && user_id) already_mapped.users.push(user_id);
                        if (!already_mapped.site_names.includes(emplacement.site)) already_mapped.site_names.push(emplacement.site);
                        if (!updated_existing_tags.includes(already_mapped.tag_id)) updated_existing_tags.push(already_mapped.tag_id);
                    }
                }

                // Check each of the other properties
                for (const [prop, value] of Object.entries(emplacement)) {
                    // Check for parasite values (ex: - / x ...)
                    if (typeof value === "string" && value.length > 0) {
                        // 1 char long non-word character
                        if (value.length === 1 && value.match(/\W/g)) add_to_list(prop, value);
                    }
                }
            }

            // Ask the user to determine which values are parasites
            const ignored = await M.renderValueIgnorer({ values: ignored_list });

            if (!ignored) resolve("cancelled");
            else {
                // Remove the parasite values from the equipments
                for (const emplacement of excel_emplacement) {
                    for (const ignore of ignored) {
                        if (emplacement[ignore.prop] === ignore.value) delete emplacement[ignore.prop];
                    }
                }

                // Recreate a list of links, that will be updated as we create new locations
                const links = emp_resources_data.data.links;
                // Sort the existing locations by type
                const existing = {
                    site: [],
                    zone: [],
                    floor: [],
                    local: [],
                    parking: [],
                    building: [],
                    emplacements: [],
                } as Record<"site" | "building" | "floor" | "local" | "zone" | "parking" | "emplacements", typeof emp_resources_data.data.locations>;

                for (const loc of emp_resources_data.data.locations) {
                    if (loc.path === FP.SITE_FORM) existing.site.push(loc);
                    else if (loc.path === FP.BUILDING_FORM) existing.building.push(loc);
                    else if (loc.path === FP.EMPLACEMENT_FORM) {
                        existing.emplacements.push(loc);
                        if (loc.type === "floor") existing.floor.push(loc);
                        else if (loc.type === "local") existing.local.push(loc);
                        else if (loc.type === "zone") existing.zone.push(loc);
                        else if (loc.type === "parking") existing.parking.push(loc);
                    }
                }

                // Check if there is already an existing site for those found in the excel file, if not, prepare the creation of one
                const new_site_data = [] as T.SiteData[];
                const site_mapper = [] as Record<"excel_name" | "_id", string>[];
                const building_mapper = [] as Record<"excel_name" | "_id" | "site_id" | "site_name", string>[];

                for (const emplacement of excel_emplacement) {
                    // Search for the site in the mapper
                    let site_mapper_entry = site_mapper.find(sm => sm.excel_name === emplacement.site);
                    // Add the site to the mapper
                    if (emplacement.site) {
                        // Search if the site was already added to the mapper
                        if (!site_mapper_entry) {
                            // Search if the site already exists /* TODO: redo the order of the search here */
                            const existing_site = existing.site.find(s => s.label === emplacement.site);
                            if (existing_site) {
                                // Add the site to the mapper
                                site_mapper.push({ excel_name: emplacement.site, _id: existing_site.value });
                                site_mapper_entry = { excel_name: emplacement.site, _id: existing_site.value };
                                // Add the tags to the mapper
                                tags_mapper.forEach(tm => {
                                    if (tm.site_names.includes(emplacement.site) && !tm.site_ids.includes(existing_site.value)) tm.site_ids.push(existing_site.value);
                                });
                            }
                            else {
                                site_mapper.push({ excel_name: emplacement.site, _id: "" });
                                site_mapper_entry = { excel_name: emplacement.site, _id: "" };
                                new_site_data.push({ name: emplacement.site, color: "#6495ed", selectIcon: "602e531839c2b234bc5dc09c" });
                            }
                        }
                    }
                    // Add the building to the mapper
                    if (emplacement.building) {
                        // Search if the building was already added to the mapper
                        if (!building_mapper.some(bm => bm.excel_name === emplacement.building && bm.site_name === emplacement.site)) {
                            // Search if the building already exists
                            const existing_building = existing.building.find(b => b.label === emplacement.building && b.full_loc.includes(emplacement.site));
                            if (existing_building) building_mapper.push({
                                site_name: emplacement.site,
                                _id: existing_building.value,
                                site_id: site_mapper_entry?._id,
                                excel_name: emplacement.building,
                            });
                            else building_mapper.push({
                                _id: "",
                                site_name: emplacement.site,
                                site_id: site_mapper_entry?._id,
                                excel_name: emplacement.building,
                            });

                        }
                    }
                }

                // Create the new sites
                const create_site_promise = new Promise<T.Submission<T.SiteData>[]>((resolve, reject) => {
                    if (new_site_data.length === 0) resolve([]);
                    else {
                        const dismount = M.renderLoader();
                        S.createSubmission({ submission: new_site_data, path: FP.SITE_FORM, attach: { parent: client } }).then(res => {
                            // Update the links
                            links.push(...res.data.links);
                            // Return the created sites
                            resolve(res.data.submissions as T.Submission<T.SiteData>[]);
                        })
                            .catch(reject)
                            .finally(dismount);
                    }
                });
                // Wait for the sites to be created
                const created_sites = await create_site_promise;

                // Add the ids of the created sites to the mapper
                for (const site of created_sites) {
                    const site_mapper_entry = site_mapper.find(sm => sm.excel_name === site.data.name);
                    const building_mapper_entries = building_mapper.filter(bm => bm.site_name === site.data.name);
                    const tag_mapper_entries = tags_mapper.filter(tm => tm.site_names.includes(site.data.name) && !tm.site_ids.includes(site._id));
                    // Update the mapper
                    if (site_mapper_entry) site_mapper_entry._id = site._id;
                    building_mapper_entries.forEach(bm => bm.site_id = site._id);
                    tag_mapper_entries.forEach(tm => tm.site_ids.push(site._id));
                }

                // Separate the tags in two groups, the ones to be created and the ones to be updated
                const new_tags: T.NoteTag[] = [];
                const updated_tags: T.BulkMongoose[] = [];

                for (const tag of tags_mapper) {
                    if (!TB.mongoIdValidator(tag.tag_id)) new_tags.push({
                        global: false,
                        users: tag.users,
                        name: tag.tag_name,
                        type: tag.tag_type,
                        sites: tag.site_ids,
                    });
                    else if (updated_existing_tags.includes(tag.tag_id)) updated_tags.push({
                        updateOne: {
                            upsert: true,
                            filter: { _id: tag.tag_id },
                            update: { "data.sites": tag.site_ids, "data.users": tag.users },
                        }
                    });
                }

                const create_tag_promise = new Promise<T.Submission<T.NoteTag>[]>(async (resolve, reject) => {
                    let create_promise: ReturnType<typeof S.createSubmission> = null;
                    let update_promise: ReturnType<typeof US.bulkSubmissionAction> = null;
                    // Check if updates are needed
                    if (updated_tags.length > 0) update_promise = US.bulkSubmissionAction(updated_tags);
                    if (new_tags.length > 0) create_promise = S.createSubmission({ submission: new_tags, path: FP.NOTE_TAG_FORM });
                    // Wait for the updates and creations to be done
                    if (update_promise) await update_promise;
                    if (create_promise) create_promise
                        .then(res => resolve(res.data.submissions as T.Submission<T.NoteTag>[]))
                        .catch(reject);
                    // No creations needed
                    else resolve([]);
                });
                // Wait for the tags to be created
                const created_tags = await create_tag_promise;

                // Update the tag mapper with the created tags
                created_tags.forEach(t => {
                    const tag_mapper_entry = tags_mapper.find(tm => tm.tag_name === t.data.name);
                    if (tag_mapper_entry) tag_mapper_entry.tag_id = t._id;
                });

                // Create the new buildings
                const create_building_promise = new Promise<(Record<"building", T.Submission<T.BuildingData>> & Record<"site_id", string>)[]>((resolve, reject) => {
                    const dismount = M.renderLoader();
                    // Store all the promises to execute them all at once
                    const promises = [] as ReturnType<typeof S.createSubmission>[];
                    // Group the buildings by site name
                    const grouped = _.groupBy(building_mapper, "site_name");

                    for (const buildings of Object.values(grouped)) {
                        // Only create the buildings that have a site, but no id of their own
                        const to_create_buildings = buildings.filter(b => b.site_id && !b._id);
                        if (to_create_buildings.length === 0) continue;
                        else {
                            const site_id = buildings[0].site_id;

                            promises.push(S.createSubmission({
                                path: FP.BUILDING_FORM,
                                attach: { parent: site_id },
                                submission: to_create_buildings.map(b => ({ name: b.excel_name, color: "#f79646", selectIcon: "602e533439c2b234bc5dc09d" })) as T.BuildingData[],
                            }));
                        }
                    }

                    Promise.all(promises).then(results => {
                        const submissions = [] as Awaited<typeof create_building_promise>;
                        for (const result of results) {
                            links.push(...result.data.links);
                            for (const sub of result.data.submissions) {
                                const site_id = result.data.links.find(l => l.output === sub._id)?.input;
                                if (site_id) submissions.push({ building: sub as T.Submission<T.BuildingData>, site_id });
                            }
                        }
                        resolve(submissions);
                    })
                        .catch(reject)
                        .finally(dismount);
                });
                // Wait for the buildings to be created
                const created_buildings = await create_building_promise;
                // Add the ids of the created buildings to the mapper
                for (const building of created_buildings) {
                    const building_mapper_entry = building_mapper.find(bm => bm.excel_name === building.building.data.name && bm.site_id === building.site_id);
                    if (building_mapper_entry) building_mapper_entry._id = building.building._id;
                }

                // Here will be all the bulk emplacements to be created
                const bulk_emplacements = [] as Parameters<typeof S.bulk_emplacements>[0];
                // For each building, create / assign locals and floors
                for (const building of building_mapper) {
                    const existing_emplacements = existing.emplacements.filter(e => e.ancestors_ids.includes(building._id));
                    const single_building_emplacements = excel_emplacement.filter(e => e.building === building.excel_name && e.site === building.site_name);

                    // Map the emplacements, that are not the rows of the excel file
                    let to_import_non_row_emplacements: Awaited<ReturnType<typeof M.renderEmplacementMapper>>["mapping"] = single_building_emplacements.map(e => {
                        let zone_id: string, floor_id: string, local_id: string;

                        // There is a zone, a floor, and a local
                        if (e.zone && e.floor && e.local) {
                            // Search for an exact match of the zone name
                            let exact_match_zone = existing.zone.find(z => z.label === e.zone);
                            zone_id = exact_match_zone?.value;
                            // Search for an exact match of the floor name in the children of the zone, if zone is found and there is a floor name
                            if (exact_match_zone) {
                                // Make a list of the children of the zone
                                let children_ids = links.filter(l => l.input === exact_match_zone.value).map(l => l.output);
                                // Search for an exact match of the floor name
                                let exact_match_floor = existing.floor.find(f => f.label === e.floor && children_ids.includes(f.value));
                                if (exact_match_floor) {
                                    floor_id = exact_match_floor.value;
                                    // Make a list of the children of the floor
                                    let children_ids = links.filter(l => l.input === exact_match_floor.value).map(l => l.output);
                                    // Search for an exact match of the local name
                                    let exact_match_local = existing.local.find(l => l.label === e.local && children_ids.includes(l.value));
                                    if (exact_match_local) local_id = exact_match_local.value;
                                }
                            }
                        }
                        // There is a zone and a local
                        else if (e.zone && e.local) {
                            // Search for an exact match of the zone name
                            let exact_match_zone = existing.zone.find(z => z.label === e.zone);
                            zone_id = exact_match_zone?.value;
                            // Search for an exact match of the local name, if zone is found and there is a local name
                            if (exact_match_zone) {
                                // Make a list of the children of the zone
                                let children_ids = links.filter(l => l.input === exact_match_zone.value).map(l => l.output);
                                // Search for an exact match of the local name
                                let exact_match_local = existing.local.find(l => l.label === e.local && children_ids.includes(l.value));
                                if (exact_match_local) local_id = exact_match_local.value;
                            }
                        }
                        // There is a zone and a floor
                        else if (e.zone && e.floor) {
                            // Search for an exact match of the zone name
                            let exact_match_zone = existing.zone.find(z => z.label === e.zone);
                            zone_id = exact_match_zone?.value;
                            // Search for an exact match of the floor name, if zone is found and there is a floor name
                            if (exact_match_zone) {
                                // Make a list of the children of the floor
                                let children_ids = links.filter(l => l.input === exact_match_zone.value).map(l => l.output);
                                // Search for an exact match of the floor name
                                let exact_match_floor = existing.floor.find(f => f.label === e.floor && children_ids.includes(f.value));
                                if (exact_match_floor) floor_id = exact_match_floor.value;
                            }
                        }
                        // There is both a floor and a local
                        else if (e.floor && e.local) {
                            // Search for an exact match of the floor name
                            let exact_match_floor = existing.floor.find(f => f.label === e.floor);
                            floor_id = exact_match_floor?.value;
                            // Search for an exact match of the local name, if floor is found and there is a local name
                            if (exact_match_floor) {
                                // Make a list of the children of the floor
                                let children_ids = links.filter(l => l.input === exact_match_floor.value).map(l => l.output);
                                // Search for an exact match of the local name
                                let exact_match_local = existing.local.find(l => l.label === e.local && children_ids.includes(l.value));
                                if (exact_match_local) local_id = exact_match_local.value;
                            }
                        }
                        // There is only a floor
                        else if (e.floor) floor_id = existing.floor.find(f => f.label === e.floor)?.value;
                        // There is only a local
                        else if (e.local) local_id = existing.local.find(l => l.label === e.local)?.value;
                        // There is only a zone
                        else if (e.zone) zone_id = existing.zone.find(z => z.label === e.zone)?.value;
                        return { floor: e.floor, local: e.local, floor_id, local_id, zone_id, zone: e.zone };
                    });
                    // Reduce the list of import to avoid duplicates
                    to_import_non_row_emplacements = _.uniqBy(to_import_non_row_emplacements, e => e.zone + e.floor + e.local);

                    // Create / assign locals and floors
                    const emp_map = await M.renderEmplacementMapper({
                        links: links,
                        building: building._id,
                        building_name: building.excel_name,
                        emplacements: existing_emplacements,
                        to_import: to_import_non_row_emplacements,
                        modal: { title: `TODO: Création des emplacements pour bâtiment ${building.excel_name} (${building.site_name})` },
                    });

                    // User cancelled the mapping of that building
                    if (!emp_map) continue;
                    // Set the floor/local in "input" property, to allow use of the bulkQuickInsert of equipments
                    for (let map of emp_map.mapping) {
                        // Filter to only keep the equipments that are concerned
                        let concerned_emp = single_building_emplacements.filter(e => e.zone === map.zone && e.floor === map.floor && e.local === map.local);
                        // Update the location
                        for (let e of concerned_emp) {
                            // Pick the closest parent
                            let input = map.local_id || map.floor_id || map.zone_id || building._id;

                            for (const tag of tags_mapper) {
                                if (tag.tag_type === "emplacement") {
                                    // Check if the tag is used in the current emplacement
                                    let is_used = false;
                                    for (const tag_prop of tags_props) {
                                        const tag_name = TagAddOn[tag_prop] + e[tag_prop];
                                        if (tag.tag_name === tag_name) {
                                            is_used = true;
                                            delete e[tag_prop];
                                            break;
                                        }
                                    }
                                    // Add the tag to the list
                                    if (is_used) {
                                        if (!Array.isArray(e.tags)) e.tags = [tag.tag_id];
                                        else e.tags.push(tag.tag_id);
                                    }
                                }
                                else if (tag.tag_name === e.actif_detail) e.actif_detail = tag.tag_id;
                            }
                            // Delete the other properties that should not appear in the emplacement item
                            extra_props.forEach(prop => delete e[prop]);
                            // Add the equipment to the list to import
                            let new_emp: typeof bulk_emplacements[number] = { ...e, actifType: "other", type: "local", input };
                            bulk_emplacements.push(new_emp);
                        }
                    }
                }

                // Create the emplacements
                if (bulk_emplacements.length === 0) resolve([]);
                else S.bulk_emplacements(bulk_emplacements)
                    .then(results => resolve(results.data))
                    .catch(reject);
            }
        }
    }
});

export default local_import;